import React, { useState, useEffect } from 'react';
import {
  Dropdown,
  Option,
  Text,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { policyService } from '../../services/policyService';
import { PolicyOption } from './types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  dropdown: {
    minWidth: '300px',
  },
  helperText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px'),
  },
  errorText: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
  },
});

interface PolicyMultiSelectProps {
  selectedPolicyIds: string[];
  onSelectionChange: (policyIds: string[]) => void;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}

export const PolicyMultiSelect: React.FC<PolicyMultiSelectProps> = ({
  selectedPolicyIds,
  onSelectionChange,
  placeholder = 'Select policies...',
  helperText,
  disabled = false,
}) => {
  const styles = useStyles();
  const [policies, setPolicies] = useState<PolicyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allPolicies = await policyService.getPolicies();
      
      // Filter to only managed policies (those ending with _OD) 
      // and exclude policies that are being deleted
      const managedPolicies = allPolicies.filter(p => {
        const isManaged = p.name.endsWith('_OD');
        const isDeletingPolicy = (p as any).deletionStatus === 'pending' || (p as any).deletionStatus === 'in_progress';
        return isManaged && !isDeletingPolicy;
      });
      
      // Transform to PolicyOption format
      const options: PolicyOption[] = managedPolicies.map(p => ({
        id: p.id,
        name: p.name,
        domainCount: 0, // Not displaying domain count
        userGroup: p.userGroup,
      }));
      
      setPolicies(options);
    } catch (err) {
      console.error('Failed to load policies:', err);
      setError('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = (_event: any, data: any) => {
    onSelectionChange(data.selectedOptions);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer} data-testid="policy-multi-select-loading">
        <Spinner size="tiny" data-testid="policy-multi-select-loading-spinner" />
        <Text data-testid="policy-multi-select-loading-text">Loading policies...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container} data-testid="policy-multi-select-error">
        <Text className={styles.errorText} data-testid="policy-multi-select-error-text">{error}</Text>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="policy-multi-select">
      <Dropdown
        className={styles.dropdown}
        multiselect
        placeholder={placeholder}
        selectedOptions={selectedPolicyIds}
        onOptionSelect={handleSelectionChange}
        disabled={disabled}
        data-testid="policy-multi-select-dropdown"
      >
        {policies.map((policy, index) => (
          <Option key={policy.id} value={policy.id} text={policy.name} data-testid={`policy-multi-select-option-${index}`}>
            {policy.name}
          </Option>
        ))}
      </Dropdown>
      
      {helperText && (
        <Text className={styles.helperText} data-testid="policy-multi-select-helper-text">{helperText}</Text>
      )}
    </div>
  );
};
